import type { SanityImageSource } from "@sanity/image-url";
import cn from "classnames";

import type {
  GetProductQueryResult,
  TwoColumnImageBenefits as TwoColumnImageBenefitsSchema,
} from "@/sanity.types";
import { urlFor } from "@/sanity/utils";
import { portableTextToPlain } from "@/utils/portableText.util";

import { TwoColumnImageBenefitsCopy } from "./twoColumnImageBenefitsCopy.component";
import { TwoColumnImageBenefitsImage } from "./twoColumnImageBenefitsImage.component";

export const TwoColumnImageBenefits = ({
  block,
  layout = "default",
}: TwoColumnImageBenefitsProps) => {
  const title = typeof block?.title === "string" ? block.title.trim() : "";
  const benefitLines = block?.benefits?.filter(
    (
      row: TwoColumnBenefitRowInput | null | undefined,
    ): row is TwoColumnBenefitRowInput =>
      row != null && portableTextToPlain(row.text) !== "",
  );
  if (!block?.image || !title || !benefitLines?.length) return null;

  let imageUrl: string;
  try {
    imageUrl = urlFor(block.image as SanityImageSource)
      .width(1200)
      .height(900)
      .url();
  } catch {
    return null;
  }
  if (!imageUrl) return null;

  const isProductDetail = layout === "productDetail";

  const listStyle =
    block.benefitsListStyle === "dividers" ? "dividers" : "checkmarks";

  return (
    <section
      data-testid="two-column-image-benefits"
      className={cn("mt-0 w-full overflow-x-clip lg:mt-6", {
        "lg:bg-greyscale-background max-lg:mt-8 max-lg:bg-white max-lg:py-0 lg:py-10":
          isProductDetail,
        "bg-greyscale-background py-10": !isProductDetail,
      })}>
      <div
        className={cn("container mx-auto", {
          "max-lg:max-w-none": isProductDetail,
          "px-4 sm:px-6 md:px-8 lg:px-12": !isProductDetail,
        })}>
        <div
          className={cn({
            "flex flex-col gap-0 px-0 lg:flex-row lg:items-center lg:gap-12 lg:px-8":
              isProductDetail,
            "mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-y-10 lg:grid-cols-2 lg:gap-x-12 lg:gap-y-0":
              !isProductDetail,
          })}>
          <TwoColumnImageBenefitsImage
            imageUrl={imageUrl}
            alt={block.image.alt ?? title}
            isProductDetail={isProductDetail}
          />
          <TwoColumnImageBenefitsCopy
            title={title}
            lines={benefitLines}
            listStyle={listStyle}
            legalText={block.legalText}
            isProductDetail={isProductDetail}
          />
        </div>
      </div>
    </section>
  );
};

/** Matches `twoColumnImageBenefits { ... }` in `getProductQuery` (differs from schema / page blocks). */
export type TwoColumnImageBenefitsFromProduct = NonNullable<
  NonNullable<GetProductQueryResult>["twoColumnImageBenefits"]
>;

export type TwoColumnImageBenefitsProps = {
  block:
    | TwoColumnImageBenefitsFromProduct
    | ({ _key: string } & TwoColumnImageBenefitsSchema)
    | null
    | undefined;
  /** PDP: image width matches the product gallery column (two thumbnails + `gap-4`). */
  layout?: "default" | "productDetail";
};

export type TwoColumnBenefitRowInput = NonNullable<
  NonNullable<TwoColumnImageBenefitsProps["block"]>["benefits"]
>[number];
